import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { FormOptionsDto, UserDto, UserInput, UserListDto } from "@delta/shared";
import { useLang, type T } from "../i18n";
import { useSession } from "../lib/session";
import { api, ApiFailure } from "../lib/api";
import { errorText } from "../lib/errors";
import { Icon } from "../components/Icon";
import { PageHead, Shell } from "../components/Shell";
import { RowMenu } from "../components/RowMenu";
import { Confirm, Field, Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { DemoLockNotice } from "../components/Demo";

// Settings › Users, ported from the prototype (erp-settings.js "users") and wired to /api/users.


const FIELD_MSG: Record<string, string> = { required: "requiredField", mobile_format: "mobileFormat", password_rule: "passwordRule", copies_range: "copiesRange", printer_required: "printerRequired", phone_taken: "phoneTaken", not_found: "requiredField", invalid: "requiredField" };

type Draft = Omit<UserInput, "ticketCopies"> & { ticketCopies: number | "" };

function UserForm({ user, onClose, onSaved }: { user: UserDto | null; onClose(): void; onSaved(u: UserDto, created: boolean): void }) {
  const { t, L } = useLang();
  const s = useSession();
  const [opts, setOpts] = useState<FormOptionsDto | null>(null);
  const [v, setV] = useState<Draft>(() => user
    ? { name: user.name, phone: user.phone, password: "", branchId: user.branchId ?? "", warehouseIds: user.warehouseIds, treasuryIds: user.treasuryIds, ticketPrinterId: user.ticketPrinterId, ticketCopies: user.ticketCopies, ticketPrinting: user.ticketPrinting, active: user.active }
    : { name: "", phone: "", password: "", branchId: s.branch !== "all" ? s.branch : s.me?.scope.branchId ?? "", warehouseIds: [], treasuryIds: [], ticketPrinterId: null, ticketCopies: 1, ticketPrinting: true, active: true });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api<FormOptionsDto>("GET", "/users/form-options").then(setOpts, (e) => setAlert(errorText(t, e))); }, [t]);
  const set = <K extends keyof Draft>(k: K, x: Draft[K]) => { setV((o) => ({ ...o, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };
  const toggle = (k: "warehouseIds" | "treasuryIds", id: string, on: boolean) => set(k, on ? [...v[k], id] : v[k].filter((x) => x !== id));
  const self = user?.id === s.me?.user.id;

  async function save() {
    const e: Record<string, string> = {};
    if (!v.name.trim()) e.name = t("requiredField");
    if (!v.phone.trim()) e.phone = t("requiredField");
    else if (!/^05\d{8}$/.test(v.phone.trim())) e.phone = t("mobileFormat");
    if (!user && !v.password) e.password = t("requiredField");
    if (!v.branchId) e.branchId = t("requiredField");
    if (!v.treasuryIds.length) e.treasuryIds = t("requiredField");
    if (!v.warehouseIds.length) e.warehouseIds = t("requiredField");
    if (v.ticketPrinting && !v.ticketPrinterId) e.ticketPrinterId = t("printerRequired");
    if (!(Number.isInteger(v.ticketCopies) && +v.ticketCopies >= 1 && +v.ticketCopies <= 5)) e.ticketCopies = t("copiesRange");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try {
      const body: UserInput = { ...v, name: v.name.trim(), phone: v.phone.trim(), ticketCopies: Number(v.ticketCopies), ticketPrinterId: v.ticketPrinterId || null };
      const out = user ? await api<UserDto>("PUT", `/users/${user.id}`, body) : await api<UserDto>("POST", "/users", body);
      onSaved(out, !user);
    } catch (err) {
      if (err instanceof ApiFailure && Object.keys(err.fields).length) setErrs(Object.fromEntries(Object.entries(err.fields).map(([k, c]) => [k, t(FIELD_MSG[c] ?? "requiredField")])));
      setAlert(errorText(t, err));
    } finally {
      setBusy(false);
    }
  }

  const multi = (k: "warehouseIds" | "treasuryIds", list: { id: string; nameEn: string; nameAr: string }[]) => (
    <div className="multi-check">{list.map((x) => (
      <label key={x.id} className="check"><input type="checkbox" checked={v[k].includes(x.id)} onChange={(e) => toggle(k, x.id, e.target.checked)} /> {L(x)}</label>
    ))}</div>
  );
  return (
    <Modal title={t(user ? "editUser" : "newUser")} sub={user?.administrator ? t("adminSeesAll") : undefined} wide onClose={onClose} onSubmit={save} alert={alert} busy={busy}>
      <Field label={t("name")} req error={errs.name} htmlFor="ff-name"><input id="ff-name" className="control" value={v.name} onChange={(e) => set("name", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("enterPhone")} req error={errs.phone} htmlFor="ff-phone"><input id="ff-phone" className="control num" style={{ textAlign: "start" }} type="tel" placeholder="05XXXXXXXX" value={v.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("password")} req={!user} error={errs.password} hint={user ? t("passwordKeep") : t("passwordRule")} htmlFor="ff-password"><input id="ff-password" className="control" type="password" value={v.password ?? ""} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" /></Field>
      <Field label={t("branch")} req error={errs.branchId} htmlFor="ff-branch">
        <select id="ff-branch" className="control" value={v.branchId} onChange={(e) => set("branchId", e.target.value)}>
          <option value="">{t("choose")}</option>
          {opts?.branches.map((b) => <option key={b.id} value={b.id}>{L(b)}</option>)}
        </select>
      </Field>
      <Field label={t("treasury")} req full error={errs.treasuryIds} hint={t("allowedTreasuries")}>{multi("treasuryIds", opts?.treasuries ?? [])}</Field>
      <Field label={t("warehouse")} req full error={errs.warehouseIds} hint={t("allowedWarehouses")}>{multi("warehouseIds", opts?.warehouses ?? [])}</Field>
      <Field label={t("ticketPrinter")} error={errs.ticketPrinterId} htmlFor="ff-printer">
        <select id="ff-printer" className="control" value={v.ticketPrinterId ?? ""} onChange={(e) => set("ticketPrinterId", e.target.value || null)}>
          <option value="">{t("choose")}</option>
          {opts?.printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label={t("ticketCopies")} error={errs.ticketCopies} htmlFor="ff-copies"><input id="ff-copies" className="control num" style={{ textAlign: "start" }} type="number" min={1} max={5} value={v.ticketCopies} onChange={(e) => set("ticketCopies", e.target.value === "" ? "" : Number(e.target.value))} /></Field>
      <Field><label className="check"><input type="checkbox" checked={v.ticketPrinting} onChange={(e) => set("ticketPrinting", e.target.checked)} /> {t("ticketPrinting")}</label></Field>
      <Field><label className="check"><input type="checkbox" checked={v.active} disabled={self || user?.administrator} onChange={(e) => set("active", e.target.checked)} /> {t("activeF")}</label></Field>
    </Modal>
  );
}

export function Users() {
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [data, setData] = useState<UserListDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [tab, setTab] = useState<"all" | "on" | "off">("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [editing, setEditing] = useState<UserDto | null | "new">(null);
  const [deleting, setDeleting] = useState<UserDto | null>(null);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      setData(await api<UserListDto>("GET", `/users${s.branch !== "all" ? `?branchId=${encodeURIComponent(s.branch)}` : ""}`));
    } catch (e) {
      setFailed(errorText(t, e));
    }
  }, [s.branch, t]);
  useEffect(() => { void load(); }, [load]);

  const branchName = (id: string | null) => L(s.branches.find((b) => b.id === id)) || "–";
  const all = data?.users ?? [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return all.filter((u) => (tab === "all" || (tab === "on" ? u.active : !u.active)) && (!term || `${u.name} ${u.uid} ${u.phone}`.toLowerCase().includes(term)));
  }, [all, tab, q]);
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const cur = Math.min(page, pages);
  const slice = filtered.slice((cur - 1) * size, cur * size);
  const me = s.me!;
  const lockTag = { disabled: true, tag: t("noPermission") };
  const pill = (cls: string, label: string) => <span className={`pill ${cls}`}>{label}</span>;

  async function makeOwner(u: UserDto) {
    try { await api("PUT", `/users/${u.id}/owner`, { owner: true }); toast(t("ownerHandedOver", { x: u.name })); await load(); await s.refresh(); }
    catch (e) { toast(errorText(t, e)); }
  }
  async function doDelete(u: UserDto) {
    setDeleting(null);
    try {
      await api("DELETE", `/users/${u.id}`);
      toast(t("userDeleted", { x: u.name }));
      await load();
    } catch (e) {
      toast(errorText(t, e));
    }
  }

  const newBtn = s.can("userAdd")
    ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t("newUser")}</button>
    : <button className="btn btn-primary acc-locked" disabled aria-disabled="true" title={t("noPermissionFor", { p: "userAdd" })}><Icon name="plus" />{t("newUser")}<span className="acc-lock"><Icon name="lock" /></span></button>;

  return (
    <Shell navKey="/users-settings">
      <DemoLockNotice />
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("users")]]} title={t("users")} />
      <section className="card list-card">
        {data && (
          <div className="usage-strip"><div><Icon name="users" /><span>{t("seatsUsed", { a: data.seats.active, b: data.seats.limit })}</span></div>
            <div className="meter"><i style={{ width: `${Math.min(100, (data.seats.active / data.seats.limit) * 100)}%` }} /></div></div>
        )}
        <div className="tabs" role="tablist">
          {([["all", t("all"), all.length], ["on", t("activeF"), all.filter((u) => u.active).length], ["off", t("inactive"), all.filter((u) => !u.active).length]] as const).map(([k, label, n]) => (
            <button key={k} role="tab" aria-selected={tab === k} className={`tab ${tab === k ? "on" : ""}`} onClick={() => { setTab(k); setPage(1); }}>{label} <span className="count num">{n}</span></button>
          ))}
        </div>
        <div className="ltb">
          <div className="input-group lq"><span className="lead"><Icon name="search" /></span><input className="control" type="search" placeholder={t("searchWord")} aria-label={t("searchWord")} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} /></div>
          <span className="spacer" />
          <button className="btn btn-sm btn-ghost btn-icon" onClick={() => void load()} title={t("refreshResults")} aria-label={t("refreshResults")}><Icon name="refresh" /></button>
          {newBtn}
        </div>
        {failed ? (
          <div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div>
        ) : !data ? (
          <div className="state-box"><p>{t("loading")}</p></div>
        ) : !filtered.length ? (
          <div className="state-box"><Icon name="search" /><h3>{t("noResults")}</h3><p>{t("noResultsBody")}</p></div>
        ) : (
          <>
            <div className="table-wrap"><table className="tbl list-tbl" data-testid="users-table">
              <thead><tr><th className="w-idx">#</th><th>{t("name")}</th><th>{t("userId")}</th><th>{t("mobileNumber")}</th><th>{t("kind")}</th><th>{t("branch")}</th><th>{t("activeF")}</th><th className="end">{t("copiesNumber")}</th><th>{t("ticketPrinting")}</th><th className="end">{t("options")}</th></tr></thead>
              <tbody>{slice.map((u, i) => {
                const isMe = u.id === me.user.id;
                return (
                  <tr key={u.id} className={u.active ? "" : "muted-row"}>
                    <td className="w-idx num muted">{(cur - 1) * size + i + 1}</td>
                    <td><span className="cell-name">{u.name}</span>{isMe && <> <span className="pill">{t("you")}</span></>}</td>
                    <td><span className="num">{u.uid}</span></td>
                    <td><span className="num">{u.phone}</span></td>
                    <td>{u.owner ? <span className="pill good" title={t("administrator")} data-testid="user-owner">{t("businessOwner")}</span> : u.administrator ? pill("info", t("administrator")) : pill("", t("userKind"))}</td>
                    <td>{branchName(u.branchId)}</td>
                    <td>{u.active ? pill("good", t("activeF")) : pill("", t("inactive"))}</td>
                    <td className="end num"><span className="num">{u.ticketCopies}</span></td>
                    <td>{u.ticketPrinting ? t("yes") : t("no")}</td>
                    <td className="end">
                      <RowMenu label={`${t("options")} · ${u.name}`} items={[
                        { label: t("edit"), icon: "edit", run: () => setEditing(u), ...(s.can("userUpdate") ? {} : lockTag) },
                        { label: t("editPermission"), icon: "lock", run: () => nav(`/permission/${u.id}`), ...(s.can("userPrivileges") ? {} : lockTag) },
                        // Owner-controlled posting: the owner hands the role to another active administrator (audited).
                        ...(me.user.owner && !u.owner && u.administrator && u.active ? [{ label: t("makeBusinessOwner"), icon: "check" as const, run: () => void makeOwner(u) }] : []),
                        { label: "", sep: true },
                        { label: t("delete"), icon: "trash", danger: true, run: () => setDeleting(u),
                          ...(!s.can("userDelete") ? lockTag : isMe ? { disabled: true, title: t("cantDeleteSelf") } : u.administrator ? { disabled: true, title: t("cantDeleteAdmin") } : {}) }
                      ]} />
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
            <div className="pager">
              <span>{t("showingOf", { a: (cur - 1) * size + 1, b: (cur - 1) * size + slice.length, n: filtered.length })}</span><span className="spacer" />
              <label className="pg-size">{t("rowsPerPage")} <select className="control" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}>{[10, 25, 50].map((n) => <option key={n}>{n}</option>)}</select></label>
              <button className="btn btn-sm btn-icon" disabled={cur <= 1} onClick={() => setPage(cur - 1)} aria-label={t("prevPage")}><Icon name="chevR" className="rev" /></button>
              <span className="num">{cur} / {pages}</span>
              <button className="btn btn-sm btn-icon" disabled={cur >= pages} onClick={() => setPage(cur + 1)} aria-label={t("nextPage")}><Icon name="chevR" className="flip" /></button>
            </div>
          </>
        )}
      </section>
      {editing && <UserForm user={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={(u, created) => {
        setEditing(null);
        toast(created ? t("userAdded", { x: u.name, id: u.uid }) : t("userUpdated", { x: u.name }));
        void load();
        if (u.id === me.user.id) void s.refresh();
      }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
    </Shell>
  );
}
